import { initializeApp } from 'firebase/app';
import {
  getDatabase,
  ref,
  push,
  query,
  orderByChild,
  orderByKey,
  limitToLast,
  startAt,
  get,
  Database,
} from 'firebase/database';
import { FIREBASE_CONFIG } from '../utils/constants';
import { WeatherData } from '../types/weather';
import { FirebaseReading } from '../types/firebase';

class FirebaseService {
  private database: Database | null = null;
  private initialized = false;
  private loggedIndexHint = false;
  private liveListeners: Array<(reading: FirebaseReading) => void> = [];

  initialize() {
    if (this.initialized) return;

    try {
      const app = initializeApp(FIREBASE_CONFIG);
      this.database = getDatabase(app);
      this.initialized = true;
      console.log('✅ Firebase initialized');
    } catch (error) {
      console.error('❌ Firebase initialization error:', error);
    }
  }

  async saveReading(data: WeatherData): Promise<void> {
    if (!this.database) {
      console.warn('Firebase not initialized');
      return;
    }

    try {
      const timestamp = typeof data.timestamp === 'string' || typeof data.timestamp === 'number'
        ? data.timestamp
        : Date.now();
      const readingsRef = ref(this.database, 'weather_data/readings');
      const payload: FirebaseReading = {
        temperature: data.temperature ?? null,
        humidity: data.humidity ?? null,
        pressure: data.pressure ?? null,
        light: data.light ?? null,
        rain: data.rain ?? null,
        windSpeed: data.windSpeed ?? null,
        windDirection: data.windDirection ?? null,
        fan: data.fan ?? false,
        timestamp,
      };
      await push(readingsRef, payload);
      this.notifyLiveListeners(payload);
    } catch (error) {
      console.error('Error saving to Firebase:', error);
    }
  }

  async getHistoricalData(hours: number = 24): Promise<FirebaseReading[]> {
    if (!this.database) {
      console.warn('Firebase not initialized');
      return [];
    }

      const cutoffTime = Date.now() - hours * 60 * 60 * 1000;
      const readingsRef = ref(this.database, 'weather_data/readings');
      
    const parseSnapshot = (snapshot: any): FirebaseReading[] => {
      const readings: FirebaseReading[] = [];
      if (!snapshot?.exists?.()) return readings;

      snapshot.forEach((child: any) => {
            const data = child.val();
        const timestampValue = data?.timestamp ?? child.key ?? null;
        const parsedTimestamp =
          typeof timestampValue === 'string'
            ? parseInt(timestampValue, 10)
            : typeof timestampValue === 'number'
              ? timestampValue
              : NaN;

        if (Number.isNaN(parsedTimestamp) || parsedTimestamp < cutoffTime) {
          return;
        }

            readings.push({
              temperature: data.temperature ?? null,
              humidity: data.humidity ?? null,
              pressure: data.pressure ?? null,
              light: data.light ?? null,
              rain: data.rain ?? null,
              windSpeed: data.windSpeed ?? null,
              windDirection: data.windDirection ?? null,
              fan: data.fan ?? false,
          timestamp: parsedTimestamp,
        });
      });

      return readings.sort((a, b) => {
        const aTime = typeof a.timestamp === 'number' ? a.timestamp : parseInt(String(a.timestamp));
        const bTime = typeof b.timestamp === 'number' ? b.timestamp : parseInt(String(b.timestamp));
        return aTime - bTime;
      });
    };

    try {
      // Preferred: indexed query on timestamp
      const recentQuery = query(
        readingsRef,
        orderByChild('timestamp'),
        startAt(cutoffTime),
        limitToLast(1000)
      );
      const snapshot = await get(recentQuery);
      return parseSnapshot(snapshot);
    } catch (error) {
      if (!this.loggedIndexHint) {
        console.warn(
          'Hint: add ".indexOn": ["timestamp"] under /weather_data/readings in Firebase rules for faster queries.'
        );
        this.loggedIndexHint = true;
      }
      // Fallback: order by key (may be slower but avoids missing index errors)
      try {
        const fallbackQuery = query(readingsRef, orderByKey(), limitToLast(1000));
        const snapshot = await get(fallbackQuery);
        return parseSnapshot(snapshot);
      } catch (fallbackError) {
        console.error('Fallback historical data fetch failed:', fallbackError);
      return [];
      }
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  onLiveReading(callback: (reading: FirebaseReading) => void): () => void {
    this.liveListeners.push(callback);
    return () => {
      this.liveListeners = this.liveListeners.filter((cb) => cb !== callback);
    };
  }

  private notifyLiveListeners(reading: FirebaseReading) {
    this.liveListeners.forEach((cb) => cb(reading));
  }
}

export const firebaseService = new FirebaseService();

